'use strict';

define('vb/private/events/eventPayloadMetadata',[], () => {
  // Component/custom event rows can be data rows, but some component integrations use the same object shape to carry
  // row-scoped runtime context. This belongs at event boundaries because VB clones component/custom event payloads and
  // exposes $event to action chains. Keep this list deliberately small and reviewed. These properties are not illegal
  // app data everywhere: for example, a variable can intentionally declare an "@context" property. The issue is
  // narrower: when live row context metadata is enumerable on an event payload row, generic action assignment treats it
  // as ordinary data and copies it into variable state. If the context belongs to a disposed/reused Dynamic UI fragment
  // row, that copy can evaluate stale expressions while handling the event. Making known row-context metadata
  // non-enumerable preserves explicit access through row[prop] for compatibility, but keeps generic data-copy paths
  // such as Object.keys(), for..in, JSON serialization, and assign-variable auto-copy from walking into runtime context
  // objects.
  const EVENT_PAYLOAD_ROW_NON_ENUMERABLE_METADATA_PROPS = ['@context'];

  /**
   * Finds a property descriptor without evaluating the property.
   *
   * @param {Object} obj object whose own/prototype chain might define the property
   * @param {string} prop property to find
   * @returns {PropertyDescriptor|undefined} property descriptor
   */
  const findPropertyDescriptor = (obj, prop) => {
    let current = obj;

    while (current) {
      const descriptor = Object.getOwnPropertyDescriptor(current, prop);
      if (descriptor) {
        return descriptor;
      }
      current = Object.getPrototypeOf(current);
    }

    return undefined;
  };

  /**
   * Makes a property non-enumerable without deleting it or evaluating it.
   *
   * This helper intentionally uses property descriptors instead of reading obj[prop]. The values we are protecting can
   * be lifecycle-sensitive context/proxy objects, so simply reading or recursively inspecting them could reproduce the
   * same stale-context failure that this guard is meant to avoid.
   *
   * @param {Object} obj object that might own or inherit the metadata property
   * @param {string} prop property to make non-enumerable
   * @returns {Function|null} restore function when the descriptor was changed
   */
  const makePropertyNonEnumerable = (obj, prop) => {
    if (!obj || typeof obj !== 'object') {
      return null;
    }

    const ownDescriptor = Object.getOwnPropertyDescriptor(obj, prop);

    // Non-configurable own descriptors may have been intentionally defined by a component or browser object; preserve
    // them rather than risking a TypeError or changing ownership. Configurable own descriptors can be adjusted
    // directly.
    if (ownDescriptor) {
      if (ownDescriptor.enumerable && ownDescriptor.configurable) {
        Object.defineProperty(obj, prop, Object.assign({}, ownDescriptor, { enumerable: false }));
        return () => Object.defineProperty(obj, prop, ownDescriptor);
      }
      return null;
    }

    const descriptor = findPropertyDescriptor(Object.getPrototypeOf(obj), prop);

    // cloneObject uses for..in, so inherited enumerable metadata is just as dangerous as own enumerable metadata.
    // Shadow the inherited descriptor with a non-enumerable own descriptor, preserving explicit row[prop] access
    // without reading the property value. If the row is not extensible, leave it alone rather than throwing while
    // handling an event.
    if (descriptor && descriptor.enumerable && Object.isExtensible(obj)) {
      Object.defineProperty(obj, prop, Object.assign({}, descriptor, { enumerable: false, configurable: true }));
      return () => {
        Reflect.deleteProperty(obj, prop);
      };
    }

    return null;
  };

  /**
   * Returns possible event payload envelopes.
   *
   * There are two narrow supported shapes:
   * - DOM/component event shape exposed to action chains as $event.detail.payload.row.
   * - Custom-event action payload shape before it is raised as a DOM event: payload.payload.row.
   *
   * Do not broaden this to arbitrary row-looking objects. Outside these event envelopes, "@context" can be legitimate
   * app data and should remain enumerable unless a caller has explicitly defined it otherwise.
   *
   * @param {Object} eventPayload event object or payload exposed as $event
   * @returns {Object[]} possible event payload envelopes
   */
  const getEventPayloadEnvelopes = (eventPayload) => [
    eventPayload && eventPayload.detail,
    eventPayload,
  ];

  /**
   * Returns the row from an event payload envelope, when present.
   *
   * @param {Object} envelope possible event payload envelope
   * @returns {Object|undefined} event payload row
   */
  const getRowFromEnvelope = (envelope) => {
    const row = envelope && envelope.payload && envelope.payload.row;
    return row && typeof row === 'object' ? row : undefined;
  };

  /**
   * Returns all conventional event payload rows, when present.
   *
   * @param {Object} eventPayload event object or payload exposed as $event
   * @returns {Object[]} event payload rows
   */
  const getEventPayloadRows = (eventPayload) => {
    const rows = [];
    getEventPayloadEnvelopes(eventPayload).forEach((envelope) => {
      const row = getRowFromEnvelope(envelope);
      if (row && rows.indexOf(row) === -1) {
        rows.push(row);
      }
    });

    return rows;
  };

  /**
   * Normalize row-scoped runtime metadata on event payloads before cloning or exposing the event to action chains.
   *
   * Keep this boundary-scoped. Do not sanitize arbitrary variables, fragment parameters, or Dynamic UI layout context:
   * those can legitimately carry runtime objects by reference. Component/custom events are different because authors
   * commonly assign $event.detail.payload into variables, and assignment/clone code enumerates source data by design.
   *
   * Current known case: Dynamic UI row context can appear as an enumerable row["@context"] on a component/custom event
   * payload. That property can point back to row-scoped expression context owned by a fragment. If an event is handled
   * after the original fragment row context has been disposed, copying the enumerable metadata can evaluate a stale
   * expression.
   *
   * @param {Object} eventPayload event object or payload exposed as $event
   */
  const normalizeEventPayloadRowContextMetadata = (eventPayload) => {
    getEventPayloadRows(eventPayload).forEach((row) => {
      EVENT_PAYLOAD_ROW_NON_ENUMERABLE_METADATA_PROPS.forEach((prop) => {
        makePropertyNonEnumerable(row, prop);
      });
    });
  };

  /**
   * Returns whether an event payload contains row metadata that this module can safely hide from enumeration.
   *
   * @param {Object} eventPayload event object or payload exposed as $event
   * @returns {boolean} true when the payload has normalizable row metadata
   */
  const canNormalizeEventPayloadRowContextMetadata = (eventPayload) => getEventPayloadRows(eventPayload).some((row) => (
    EVENT_PAYLOAD_ROW_NON_ENUMERABLE_METADATA_PROPS.some((prop) => {
      const ownDescriptor = Object.getOwnPropertyDescriptor(row, prop);
      if (ownDescriptor) {
        return ownDescriptor.enumerable && ownDescriptor.configurable;
      }

      const descriptor = findPropertyDescriptor(Object.getPrototypeOf(row), prop);
      return !!(descriptor && descriptor.enumerable && Object.isExtensible(row));
    })
  ));

  /**
   * Temporarily hides row-scoped runtime metadata while a caller performs a clone/copy operation.
   *
   * This is for callers that receive app-owned payload objects by reference but still need to clone without enumerating
   * lifecycle-sensitive row metadata. The original descriptors are restored before returning, so the caller-owned
   * payload is not left with changed enumerability.
   *
   * @param {Object} eventPayload event object or payload exposed as $event
   * @param {Function} callback operation to run while metadata is non-enumerable
   * @returns {*} callback result
   */
  const withEventPayloadRowContextMetadataHidden = (eventPayload, callback) => {
    const restoreCallbacks = [];

    getEventPayloadRows(eventPayload).forEach((row) => {
      EVENT_PAYLOAD_ROW_NON_ENUMERABLE_METADATA_PROPS.forEach((prop) => {
        const restore = makePropertyNonEnumerable(row, prop);
        if (restore) {
          restoreCallbacks.push(restore);
        }
      });
    });

    try {
      return callback();
    } finally {
      restoreCallbacks.reverse().forEach((restore) => restore());
    }
  };

  /**
   * Creates a metadata descriptor for a cloned row without evaluating the original property.
   *
   * Accessor-backed metadata can depend on the original row as its receiver. If the clone used the accessor descriptor
   * directly, explicit clonedRow[prop] access would call the getter with the cloned row as "this". Bind accessors back
   * to the source row instead so explicit access keeps the original row semantics without making clone paths evaluate
   * the metadata eagerly.
   *
   * @param {PropertyDescriptor} descriptor source property descriptor
   * @param {Object} sourceRow original event payload row
   * @returns {PropertyDescriptor} descriptor safe to define on the cloned row
   */
  const createClonedPropertyDescriptor = (descriptor, sourceRow) => {
    if (Object.prototype.hasOwnProperty.call(descriptor, 'value')) {
      return descriptor;
    }

    const clonedDescriptor = Object.assign({}, descriptor);
    if (descriptor.get) {
      clonedDescriptor.get = () => descriptor.get.call(sourceRow);
    }
    if (descriptor.set) {
      clonedDescriptor.set = (value) => descriptor.set.call(sourceRow, value);
    }

    return clonedDescriptor;
  };

  /**
   * Preserves explicit access to row-scoped runtime metadata after cloning an event payload.
   *
   * Some event paths clone the event after normalization. Non-enumerable metadata is intentionally skipped by the
   * clone, so copy the original property descriptor to the cloned row without reading the metadata value. The cloned
   * event keeps compatibility for explicit row[prop] consumers while still hiding the metadata from enumeration.
   *
   * @param {Object} sourceEventPayload original event object or payload exposed as $event
   * @param {Object} clonedEventPayload cloned event object or payload exposed as $event
   */
  const preserveEventPayloadRowContextMetadata = (sourceEventPayload, clonedEventPayload) => {
    const sourceEnvelopes = getEventPayloadEnvelopes(sourceEventPayload);
    const clonedEnvelopes = getEventPayloadEnvelopes(clonedEventPayload);

    sourceEnvelopes.forEach((sourceEnvelope, index) => {
      const sourceRow = getRowFromEnvelope(sourceEnvelope);
      const clonedRow = getRowFromEnvelope(clonedEnvelopes[index]);

      if (!sourceRow || !clonedRow) {
        return;
      }

      EVENT_PAYLOAD_ROW_NON_ENUMERABLE_METADATA_PROPS.forEach((prop) => {
        const descriptor = findPropertyDescriptor(sourceRow, prop);
        if (descriptor && !Object.prototype.hasOwnProperty.call(clonedRow, prop) && Object.isExtensible(clonedRow)) {
          Object.defineProperty(clonedRow, prop, createClonedPropertyDescriptor(descriptor, sourceRow));
        }
      });
    });
  };

  /**
   * Clones an event payload while hiding row-scoped runtime metadata from enumeration during the clone.
   *
   * The source payload descriptors are restored before returning. The clone keeps explicit row[prop] access to metadata
   * without making that metadata enumerable.
   *
   * @param {Object} payload event payload
   * @param {Function} cloneCallback callback that performs the actual clone
   * @returns {Object} cloned payload
   */
  const cloneEventPayloadWithRowContextMetadataHidden = (payload, cloneCallback) => {
    let clonePayload = payload;

    if (payload) {
      withEventPayloadRowContextMetadataHidden(payload, () => {
        clonePayload = cloneCallback(payload);
        preserveEventPayloadRowContextMetadata(payload, clonePayload);
      });
    }

    return clonePayload;
  };

  return {
    canNormalizeEventPayloadRowContextMetadata,
    cloneEventPayloadWithRowContextMetadataHidden,
    normalizeEventPayloadRowContextMetadata,
    preserveEventPayloadRowContextMetadata,
  };
});

